import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { CContainer, CRow, CCol, CCard, CCardBody, CForm, CFormInput, CButton, CSpinner, CAlert } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilReload } from '@coreui/icons';
import { useCaptcha } from '../../hooks/useCaptcha';
import { useTranslation } from 'react-i18next';

const RegistrationPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', organization_name: '',
    first_name: '', last_name: '', captcha: '', 
  });
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const navigate = useNavigate();
  const { loadingCaptcha, canvasRef, fetchCaptcha, countdown } = useCaptcha();

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.email || !formData.password || !formData.organization_name || !formData.captcha) {
      toast.warn(t('registration.fill_required'));
      return;
    }
    setLoading(true);
    try {
      await axios.post('/api/register/', formData);
      setRegistrationSuccess(true);
    } catch (error) {
      const errors = error.response?.data;
      if (errors) {
        if (errors.captcha) {
          toast.error(errors.captcha[0]);
          fetchCaptcha(); 
          setFormData(f => ({ ...f, captcha: '' }));
        }
        Object.entries(errors).forEach(([key, value]) => {
          if(key !== 'captcha') {
            toast.error(`${key}: ${value[0]}`);
          }
        });
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol md={8}>
              <CCard className="p-4">
                <CCardBody className="text-center">
                  <CAlert color="success">
                    <h2>{t('registration.success_title')}</h2>
                    <p className="mt-3">
                      {t('registration.success_msg_1')} <strong>{formData.email}</strong>. {t('registration.success_msg_2')}
                    </p>
                    <hr />
                    <p className="mb-0">
                      {t('registration.success_msg_3')} <a href="https://t.me/neimarker" target="_blank" rel="noopener noreferrer">@neimarker</a>.
                    </p>
                  </CAlert>
                  <CButton color="primary" onClick={() => navigate('/login')}>
                    {t('registration.back_login')}
                  </CButton>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    );
  }

  const minutes = Math.floor(countdown / 60);
  const seconds = String(countdown % 60).padStart(2, '0');

  return (
    <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={8}>
            <CCard className="mx-4">
              <CCardBody className="p-4">
                <CForm onSubmit={handleSubmit}>
                  <h1>{t('registration.title')}</h1>
                  <p className="text-medium-emphasis">{t('registration.subtitle')}</p>
                  <CFormInput className="mb-3" placeholder={t('registration.username_ph')} name="username" value={formData.username} onChange={handleInputChange} required />
                  <CFormInput className="mb-3" placeholder={t('registration.email_ph')} type="email" name="email" value={formData.email} onChange={handleInputChange} required />
                  <CFormInput className="mb-3" placeholder={t('registration.password_ph')} type="password" name="password" value={formData.password} onChange={handleInputChange} required minLength={8} />
                  <CFormInput className="mb-3" placeholder={t('registration.org_ph')} name="organization_name" value={formData.organization_name} onChange={handleInputChange} required />
                  <CFormInput className="mb-3" placeholder={t('registration.firstname_ph')} name="first_name" value={formData.first_name} onChange={handleInputChange} />
                  <CFormInput className="mb-3" placeholder={t('registration.lastname_ph')} name="last_name" value={formData.last_name} onChange={handleInputChange} />
                  
                  <div className="mb-3">
                    <CRow className="align-items-center mb-2">
                      <CCol><label className="form-label">{t('registration.captcha_label')}</label></CCol>
                      <CCol className="text-end">
                          {countdown > 0 ? (
                              <small className="text-muted">
                                  {t('login.expires_in')}: {minutes}:{seconds}
                              </small>
                          ) : (
                              !loadingCaptcha && <small className="text-danger">{t('login.captcha_expired')}</small>
                          )}
                      </CCol>
                    </CRow>
                    <CRow className="align-items-center">
                      <CCol xs="auto">
                        <div style={{ border: '1px solid #ccc', borderRadius: '4px', height: '62px', width: '152px', background: '#f0f0f0', position: 'relative' }}>
                          {loadingCaptcha && (
                            <div className="d-flex justify-content-center align-items-center h-100">
                              <CSpinner size="sm" />
                            </div>
                          )}
                          <canvas ref={canvasRef} style={{ display: loadingCaptcha ? 'none' : 'block' }} />
                        </div>
                      </CCol>
                      <CCol xs="auto">
                        <CButton color="light" onClick={() => fetchCaptcha(false)} title={t('login.update_image')} disabled={loadingCaptcha}>
                          <CIcon icon={cilReload} />
                        </CButton>
                      </CCol>
                    </CRow>
                    <CFormInput 
                      className="mt-2" name="captcha" value={formData.captcha} 
                      onChange={handleInputChange} required autoComplete="off"
                    />
                  </div>

                  <div className="d-grid">
                    <CButton color="success" type="submit" disabled={loading}>
                      {loading ? <CSpinner size="sm" /> : t('registration.create_btn')}
                    </CButton>
                  </div>
                  <CRow className="text-center mt-3">
                    <CCol>
                      <Link to="/login">{t('registration.has_account')}</Link>
                    </CCol>
                  </CRow>
                </CForm>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default RegistrationPage;